import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/axios';
import { toast } from 'react-toastify';
import {
    LockClosedIcon,
    BriefcaseIcon,
    KeyIcon,
    ShieldCheckIcon,
    ArrowRightIcon
} from '@heroicons/react/24/outline';

// Reusable Logo Component
const Logo = ({ className, textClass = "text-gray-900" }) => (
    <div className={`flex items-center space-x-3 ${className}`}>
        <div className="bg-primary/10 p-2 rounded-xl">
            <BriefcaseIcon className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
        </div>
        <span className={`text-xl sm:text-2xl font-bold tracking-tight ${textClass}`}>SmartBreak</span>
    </div>
);

const ResetPassword = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirmPassword) return toast.error("Passwords do not match");
        if (password.length < 6) return toast.error("Password must be at least 6 characters");

        setLoading(true);
        try {
            await api.post('/auth/reset-password', { token, newPassword: password });
            toast.success("Password reset successful! Please login.");
            navigate('/');
        } catch (err) {
            toast.error(err.response?.data?.error || "Token invalid or expired");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex bg-white font-sans">

            {/* --- LEFT SIDE: BRAND & VISUALS (Hidden on mobile/tablet) --- */}
            <div className="hidden lg:flex lg:w-1/2 xl:w-7/12 bg-slate-900 relative items-center justify-center overflow-hidden">
                {/* Background Effects */}
                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-slate-900 to-slate-800 z-0"></div>
                <div className="absolute top-20 -left-20 w-96 h-96 bg-indigo-600 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-pulse"></div>
                <div className="absolute bottom-20 -right-20 w-96 h-96 bg-emerald-600 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-pulse"></div>

                <div className="relative z-10 p-12 text-white max-w-xl">
                    {/* Glass Badge */}
                    <div className="bg-white/10 backdrop-blur-md p-3 pr-5 rounded-2xl inline-flex items-center space-x-3 mb-8 shadow-2xl border border-white/10">
                        <div className="bg-emerald-500/20 p-2 rounded-xl border border-white/10">
                            <ShieldCheckIcon className="h-6 w-6 text-emerald-400" />
                        </div>
                        <div>
                            <span className="text-sm font-bold tracking-wide text-emerald-100 uppercase">Account Security</span>
                        </div>
                    </div>

                    <h1 className="text-4xl xl:text-5xl font-bold mb-6 tracking-tight leading-tight">
                        Secure your <br /> workspace access.
                    </h1>
                    <p className="text-slate-400 text-lg leading-relaxed">
                        Create a strong, unique password to protect your personal data and company information within SmartBreak.
                    </p>
                </div>
            </div>

            {/* --- RIGHT SIDE: FORM --- */}
            <div className="w-full lg:w-1/2 xl:w-5/12 flex items-center justify-center p-6 sm:p-12 bg-white">
                <div className="w-full max-w-sm sm:max-w-md space-y-8">

                    {/* Mobile Brand Header */}
                    <div className="lg:hidden mb-8 flex justify-center">
                        <Logo />
                    </div>

                    <div className="text-center lg:text-left">
                        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Reset Password</h2>
                        <p className="mt-2 text-sm sm:text-base text-slate-500">Enter your new credentials below.</p>
                    </div>

                    <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                        <div className="space-y-5">

                            {/* New Password */}
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1.5">New Password</label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <KeyIcon className="h-5 w-5 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                                    </div>
                                    <input
                                        type="password"
                                        required
                                        className="block w-full pl-10 pr-3 py-3 border border-slate-200 rounded-xl leading-5 bg-slate-50 text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all duration-200 text-base sm:text-sm"
                                        placeholder="Min 6 characters"
                                        onChange={e => setPassword(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* Confirm Password */}
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1.5">Confirm Password</label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <LockClosedIcon className="h-5 w-5 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                                    </div>
                                    <input
                                        type="password"
                                        required
                                        className="block w-full pl-10 pr-3 py-3 border border-slate-200 rounded-xl leading-5 bg-slate-50 text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all duration-200 text-base sm:text-sm"
                                        placeholder="Repeat new password"
                                        onChange={e => setConfirmPassword(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={loading}
                            className={`w-full flex justify-center items-center py-3.5 px-4 border border-transparent rounded-xl shadow-lg shadow-indigo-500/20 text-sm font-bold text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all transform hover:scale-[1.01] active:scale-[0.98] ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                            {loading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Updating...
                                </>
                            ) : (
                                <>Reset Password <ArrowRightIcon className="ml-2 h-4 w-4" /></>
                            )}
                        </button>
                    </form>

                    <div className="mt-6 pt-6 border-t border-slate-100 text-center">
                        <p className="text-xs text-slate-400">
                            Secure connections provided by SmartBreak Systems Inc.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;